import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { color, space, fontSize, radius, focusRingStyle } from './tokens';
function ActionButton({ action, onAction, disabled = false, }) {
    // Interaction states are applied via event handlers (no CSS is shipped by
    // px-react; visual values stay 100% token-sourced). This is the same
    // token-driven, inline-style approach already used by Navigation. NOTE: this
    // is :focus, not true :focus-visible — see the a11y note in states' tests and
    // the adoption doc; a genuine :focus-visible needs a stylesheet (out of scope).
    const base = {
        marginTop: space('md'),
        padding: `${space('sm')} ${space('md')}`,
        background: color('primary'),
        color: color('onPrimary'),
        border: 'none',
        borderRadius: radius('sm'),
        cursor: disabled ? 'not-allowed' : 'pointer',
        // Disabled affordance without a raw color: dim the token-colored surface.
        opacity: disabled ? 0.6 : 1,
    };
    return (_jsx("button", { type: "button", disabled: disabled, "aria-disabled": disabled || undefined, onClick: () => {
            if (!disabled)
                onAction?.(action.intentRef);
        }, onFocus: (e) => {
            if (!disabled)
                Object.assign(e.currentTarget.style, focusRingStyle);
        }, onBlur: (e) => {
            e.currentTarget.style.outline = '';
            e.currentTarget.style.outlineOffset = '';
        }, onMouseEnter: (e) => {
            if (!disabled)
                e.currentTarget.style.opacity = '0.92';
        }, onMouseLeave: (e) => {
            if (!disabled)
                e.currentTarget.style.opacity = '1';
        }, style: base, children: action.label }));
}
const container = {
    padding: space('lg'),
    color: color('text'),
    fontSize: fontSize('sizeMd'),
    textAlign: 'center',
};
export function CanonicalState({ model, onAction, actionDisabled = false, icon }) {
    // Fail-safe: unknown/missing kind renders a neutral loading surface, never a broken one.
    const kind = model?.kind ?? 'loading';
    // Decorative only: hidden from assistive tech. Rendered for the content-rich
    // empty/error states; loading/recovery are terse status lines.
    const iconNode = icon ? (_jsx("span", { "aria-hidden": "true", style: { display: 'block', marginBottom: space('sm') }, children: icon })) : null;
    if (kind === 'loading') {
        return (_jsx("div", { role: "status", "aria-busy": "true", "aria-live": "polite", style: container, children: _jsx("span", { children: model?.title ?? 'Loading…' }) }));
    }
    if (kind === 'recovery') {
        return (_jsx("div", { role: "status", "aria-busy": "true", "aria-live": "polite", style: container, children: _jsx("span", { children: model?.title ?? 'Recovering…' }) }));
    }
    if (kind === 'error') {
        return (_jsxs("div", { role: "alert", style: container, children: [iconNode, _jsx("strong", { children: model?.title ?? 'Something went wrong' }), model?.description && _jsx("p", { style: { color: color('muted') }, children: model.description }), model?.action && _jsx(ActionButton, { action: model.action, onAction: onAction, disabled: actionDisabled })] }));
    }
    // empty
    return (_jsxs("div", { style: container, children: [iconNode, _jsx("strong", { children: model?.title ?? 'Nothing here yet' }), model?.description && _jsx("p", { style: { color: color('muted') }, children: model.description }), model?.action && _jsx(ActionButton, { action: model.action, onAction: onAction, disabled: actionDisabled })] }));
}
export const LoadingState = (p) => (_jsx(CanonicalState, { model: { kind: 'loading', title: p.title } }));
export const EmptyState = (p) => _jsx(CanonicalState, { ...p });
export const ErrorState = (p) => _jsx(CanonicalState, { ...p });
export const RecoveryState = (p) => (_jsx(CanonicalState, { model: { kind: 'recovery', title: p.title } }));
//# sourceMappingURL=states.js.map